import React from "react";
import { LiveQuote, useQuote, useQuoteConnectionStatus } from "../../market/QuoteStore";
import { getPricePresentation, quoteSubscriptionIdentifier } from "../../market/pricePresentation";

type QuoteReference = Partial<LiveQuote> & {
    contract_key?: string;
    sequence?: number;
};

interface LiveStrategyPriceProps {
    symbol: string;
    evaluatedPrice?: number | null;
    evaluatedQuote?: QuoteReference | null;
    initialLiveQuote?: QuoteReference | null;
    decisionPrice?: number | null;
    decisionSource?: string | null;
    decisionLabel?: string | null;
    executionPrice?: number | null;
    executionQuote?: QuoteReference | null;
    executionLabel?: string | null;
    streamedQuoteOverride?: LiveQuote | null;
    priceClassName?: string;
}

const money = (value: unknown) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? `$${numeric.toFixed(2)}` : "—";
};

const LiveStrategyPrice: React.FC<LiveStrategyPriceProps> = ({
    symbol,
    evaluatedPrice,
    evaluatedQuote,
    initialLiveQuote,
    decisionPrice,
    decisionSource,
    decisionLabel,
    executionPrice,
    executionQuote,
    executionLabel,
    streamedQuoteOverride,
    priceClassName = "text-white",
}) => {
    const connectionStatus = useQuoteConnectionStatus();
    const streamedQuoteFromStore = useQuote(
        streamedQuoteOverride === undefined
            ? quoteSubscriptionIdentifier(symbol, evaluatedQuote)
            : null
    );
    const streamedQuote = streamedQuoteOverride === undefined
        ? streamedQuoteFromStore
        : streamedQuoteOverride;
    const separateExecutionContract = Boolean(
        executionPrice != null
        && executionQuote?.contract_key
        && executionQuote.contract_key !== evaluatedQuote?.contract_key
    );
    const streamedExecutionQuote = useQuote(
        separateExecutionContract
            ? quoteSubscriptionIdentifier(executionQuote?.symbol || symbol, executionQuote)
            : null
    );
    const liveQuote = streamedQuote || initialLiveQuote;
    const livePrice = liveQuote?.price ?? evaluatedPrice;
    const hasDisplayedPrice = Number.isFinite(Number(livePrice)) && Number(livePrice) > 0;
    const displayedQuote = liveQuote || evaluatedQuote;
    const presentation = getPricePresentation({
        quote: displayedQuote,
        connectionStatus,
        hasPrice: hasDisplayedPrice,
        streamed: Boolean(streamedQuote),
    });
    const executionPresentation = getPricePresentation({
        quote: streamedExecutionQuote || executionQuote,
        connectionStatus,
        hasPrice: executionPrice != null,
        streamed: Boolean(streamedExecutionQuote),
    });

    return (
        <div className="flex flex-col items-end gap-0.5 leading-tight">
            <div className="flex items-center gap-1.5">
                <span className={`font-semibold ${priceClassName}`}>{money(livePrice)}</span>
                <span className={`rounded border px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide ${presentation.badgeClassName}`}>
                    {presentation.status}
                </span>
            </div>
            <span
                className="text-[9px] text-white/45"
                title={evaluatedQuote?.received_at || evaluatedQuote?.exchange_time || undefined}
            >
                Engine {money(evaluatedPrice)}
                {evaluatedQuote?.sequence != null ? ` · #${evaluatedQuote.sequence}` : ""}
            </span>
            {decisionPrice != null && (
                <span className="text-[9px] text-sky-300/70" title={decisionSource || undefined}>
                    {decisionLabel || "Decision"} {money(decisionPrice)}
                </span>
            )}
            {executionPrice != null && separateExecutionContract && (
                <span className="text-[9px] text-neon/80">
                    {executionLabel || "Exec"} {money(streamedExecutionQuote?.price ?? executionPrice)}
                    {streamedExecutionQuote
                        ? ` · ${executionPresentation.status.toLowerCase()}`
                        : ""}
                </span>
            )}
            {executionPrice != null && (
                <span
                    className="text-[9px] text-neon/70"
                    title={executionQuote?.received_at || executionQuote?.exchange_time || undefined}
                >
                    {separateExecutionContract ? "Sizing ref" : "Exec ref"} {money(executionPrice)}
                    {executionQuote?.sequence != null ? ` · #${executionQuote.sequence}` : ""}
                </span>
            )}
        </div>
    );
};

export default LiveStrategyPrice;
